import { getProblemLabel } from '@/src/utils/problemLabel'
import { capitalizeDrugName, concatenateStrengthsConcise } from '@/src/utils'
import { useMedical, useMedication } from '@hook'
import { MedicationDataMock } from '@type/medication.type'
import { ActiveTab, Badge, SideDrawer } from '@ui'
import InformationList from '@ui/InformationList'
import { Box, Spinner, Text } from 'grommet'
import moment from 'moment'
import { useMemo, useState } from 'react'

type Props = {
  onClose: () => void
  defaultData?: MedicationDataMock
  embedded?: boolean
}

const detailOptions = ['order details', 'patient Details', 'notes']

export const pharmacyMedStatus = [
  { label: 'Pending', value: 'pending' },
  { label: 'Under Review', value: 'under_review' },
  { label: 'Verified', value: 'verified' }
]

export const MedecationDetail = ({ onClose, defaultData, embedded = false }: Props) => {
  const [tab, setTab] = useState('order details')
  const { medication, includedMed, singleMedIsLoading, patientMedication } = useMedication({
    id: defaultData?.id,
    patient: defaultData?.relationships.patient.data?.id
  })
  const currentMedication = medication ?? defaultData
  const { medicals, included } = useMedical({ patientId: defaultData?.relationships.patient.data?.id || '' })

  const drug = useMemo(() => {
    if (currentMedication && includedMed) {
      const name = includedMed?.find(
        ({ type, id }) => type === 'product' && id === currentMedication.relationships.product?.data?.id
      )?.attributes

      return name ? `${name?.generic_name} ${name?.brand_name}` : ''
    }
    return ''
  }, [currentMedication, includedMed])

  const medicationInfos = useMemo(() => {
    if (currentMedication) {
      const medicationFreq = includedMed?.find(
        ({ type, id }) =>
          type === 'medication_frequency' && id === currentMedication.relationships.medication_frequency.data?.id
      )

      const doctor = includedMed?.find(
        ({ type, id }) => type === 'staff' && id === currentMedication.relationships.staff.data?.id
      )?.attributes
      return {
        'Ordered by': `${doctor?.first_name} ${doctor?.last_name}`,
        comment: currentMedication.attributes.comment || '-',
        dosage: currentMedication.attributes.strength,
        'end date': moment(currentMedication?.attributes.end_at).format('DD/MM/YYYY'),
        form: currentMedication.attributes.form,
        frequency: medicationFreq?.attributes.code,
        medication: capitalizeDrugName(drug || currentMedication?.attributes.nf_drug_name, '--'),
        'medication type': currentMedication.attributes.medication_type.toUpperCase(),
        quantity: currentMedication.attributes.quantity || '-',
        route: currentMedication.attributes.route,
        'start date': moment(currentMedication?.attributes.start_at).format('DD/MM/YYYY')
      }
    }
    return {}
  }, [currentMedication, includedMed, drug])

  const onGoingMed: Record<string, string[]> = useMemo(() => {
    if (patientMedication) {
      return patientMedication.data.reduce<Record<string, string[]>>((acc, data, index) => {
        const drug = patientMedication.included?.find(
          ({ type, id }) => type === 'product' && id === data.relationships.product.data?.id
        )?.attributes

        console.log({ drug })

        const medicationFreq = patientMedication?.included.find(
          ({ type, id }) => type === 'medication_frequency' && id === data.relationships.medication_frequency.data?.id
        )?.attributes.code

        const form = data?.attributes?.form?.trim()
        const strength = data?.attributes?.strength || concatenateStrengthsConcise(data?.attributes.api_doses)
        const formLabel = form || strength ? `Form: ${form || '-'}, Strength: ${strength || '-'}, ` : ''
        acc[`medication${index + 1}`] = [
          `${drug?.generic_name} (${drug?.brand_name})`,
          `Qty: ${data?.attributes?.quantity || '-'}, Dosage: ${data?.attributes?.dose_quantity ? data?.attributes?.dose_quantity + ' ' + data?.attributes?.dose_unit : data?.attributes?.strength || concatenateStrengthsConcise(data?.attributes.api_doses)}`,
          `${formLabel}Frequency: ${medicationFreq || '---'} ${data.attributes.medication_type.toUpperCase()}`
        ]
        return acc
      }, {})
    }
    return {}
  }, [patientMedication])

  const patientInfo = useMemo(() => {
    if (currentMedication) {
      const patient = includedMed?.find(
        ({ type, id }) => type === 'patient' && id === currentMedication.relationships.patient.data?.id
      )

      return {
        DOB: patient?.attributes.date_of_birth,
        Name: `${patient?.attributes.first_name} ${patient?.attributes.last_name}`
      }
    }
    return {}
  }, [currentMedication, includedMed])

  const encounterInfo = useMemo(() => {
    if (currentMedication) {
      const encounter_med_history =
        includedMed?.filter(
          ({ type, id }) =>
            type === 'medical_history' &&
            currentMedication.relationships.medical_histories?.data?.some(({ id: filterId }) => id === filterId)
        ) || []

      // Now, build med_array: get full "medical" objects for each id in encounter_med_history, from the medicals fetched from useMedical
      let med_array: any[] = []
      if (Array.isArray(encounter_med_history) && Array.isArray(medicals)) {
        const encounterIds = encounter_med_history?.map(e => e?.id)
        med_array = medicals.filter((med: any) => encounterIds.includes(med?.id))
      }

      const infoObj: Record<string, string> = {}
      for (const med of med_array) {
        const diagnosis = getProblemLabel(med, included)
        if (diagnosis) {
          infoObj[diagnosis] = moment(med.attributes.created_at).format('DD/MM/YYYY') || '---'
        }
      }

      return infoObj
    }
    return {}
  }, [currentMedication, includedMed, medicals, included])

  const allergiesInfo = useMemo(() => {
    if (currentMedication) {
      const allergies = includedMed?.filter(
        ({ type, id }) => type === 'allergy' && currentMedication.relationships.allergy?.data?.id === id
      )
      return (
        allergies
          ?.map(({ attributes }) => attributes)
          ?.reduce<Record<string, string>>((acc, data, index) => {
            acc[`Drug name ${index + 1}`] = data?.title || '---'
            return acc
          }, {}) || {}
      )
    }
    return {}
  }, [currentMedication, includedMed])

  const header = (
    <Box gap={'20px'} margin={{ bottom: '10px', vertical: '16px' }}>
      <div className="flex flex-col gap-3 justify-start items-start">
        <Text size="24px" weight={600}>
          {capitalizeDrugName(drug || currentMedication?.attributes.nf_drug_name)}
        </Text>
        <Badge label={currentMedication?.meta.status.replace(/_/g, ' ')} status={currentMedication?.meta.status} />
      </div>
      {/* {medication?.meta.status === 'pending' && (
        <div className="max-w-[157px]">
          <DropdownBtn
            options={[
              ...(medication_transitions?.[medication?.meta.status]?.map(value => ({
                label: capitalizeEachLetter(value.replace(/_/g, ' ')),
                handleClick: (onClose: () => void) => {
                  onOptionClick(medication?.id, value, onClose)
                  onClose()
                }
              })) || [])
            ]}
            dropdownLabel={
              <Box direction="row" width={'140px'} justify="center" gap={'12px'} align="center">
                <Text size="small" weight={500} style={{ textTransform: 'capitalize' }}>
                  Pending
                </Text>
                <DropDownIcon />
              </Box>
            }
            style={{ padding: '10px 12px', borderRadius: '100px', border: `1px solid rgba(0, 0, 0, 0.33)` }}
            dropProps={{ margin: { left: '0px' }, align: { top: 'bottom' } }}
          />
        </div>
      )} */}
    </Box>
  )
  if (embedded) {
    return (
      <div className="flex min-w-0 flex-col gap-5">
        <ActiveTab divider options={detailOptions} setActive={setTab} />
        {singleMedIsLoading ? <Spinner size="small" /> : null}
        {tab === 'order details' && currentMedication ? <InformationList data={medicationInfos} /> : null}
        {tab === 'patient Details' && currentMedication ? (
          <div className="flex flex-col gap-4">
            <InformationList data={patientInfo} title="Patient" />
            <InformationList data={encounterInfo} title="Encounter Problem" />
            <InformationList data={allergiesInfo} title="Allergies" />
            <InformationList data={onGoingMed} title="Ongoing Medications" />
          </div>
        ) : null}
        {tab === 'notes' ? (
          <InformationList
            data={{
              Comment: currentMedication?.attributes.comment || '-',
              'Review note':
                currentMedication?.meta.query_note || currentMedication?.meta.last_transition?.metadata?.note || '-'
            }}
          />
        ) : null}
      </div>
    )
  }
  return (
    <SideDrawer
      header={header}
      onClose={onClose}
      options={detailOptions}
      setTab={setTab}
      style={{ padding: '0 40px' }}
      title="Order Details"
    >
      <Box flex style={{ position: 'relative' }}>
        <div
          className={`step-slide ${tab === 'order details' ? 'active' : ''} px-5`}
          style={{
            position: 'absolute',
            transform: `translateX(${tab === 'order details' ? '0' : '-100%'})`,
            transition: 'all 0.3s ease-in-out',
            width: '100%'
          }}
        >
          {singleMedIsLoading && (
            <Box align="center" alignSelf="center" direction="row" gap="small" justify="center" pad="large">
              <Spinner height={'40px'} size="5px" width={'40px'} />
              <Text color="white">Loading...</Text>
            </Box>
          )}
          {currentMedication && <InformationList data={medicationInfos} />}
        </div>
        <div
          className={`step-slide ${tab === 'patient Details' ? 'active' : ''} px-10`}
          style={{
            bottom: '0px',
            position: 'absolute',
            top: '0px',
            transform: `translateX(${tab === 'patient Details' ? '0' : tab === 'order details' ? '100%' : '-100%'})`,
            transition: 'all 0.3s ease-in-out',
            width: '100%'
          }}
        >
          {currentMedication && <InformationList data={patientInfo} title="Patient" />}
          {currentMedication && <InformationList data={encounterInfo} title="Encounter Problem" />}
          {currentMedication && <InformationList data={allergiesInfo} title="Allergies" />}
          {currentMedication && <InformationList data={onGoingMed} title="Ongoing Medications" />}
        </div>
        <div
          className={`step-slide ${tab === 'notes' ? 'active' : ''} px-5 inset-0 flex pb-5`}
          style={{
            position: 'absolute',
            transform: `translateX(${tab === 'notes' ? '0' : '100%'})`,
            transition: 'all 0.3s ease-in-out',
            width: '100%'
          }}
        >
          <div className="flex flex-col overflow-auto justify-between flex-1">
            {/* <Text color="#666666" size="14px">
              No notes to display
            </Text> */}
            {/* <Box pad={{ vertical: '24px', horizontal: '16px' }} flex height={'100%'} overflow={'auto'}>
              <IconButton label="Add Note" onClick={() => setShowEditor(prev => !prev)} />
            </Box>
            {showEditor && (
              <Box>
                <RichTextEditor style={{ minHeight: '100px' }} />
              </Box>
            )} */}
          </div>
        </div>
      </Box>
    </SideDrawer>
  )
}
